import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';

interface CreateOrgModalProps {
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    type: string;
    description?: string;
    billingEmail: string;
  }) => Promise<void>;
}

export const CreateOrgModal: React.FC<CreateOrgModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'other',
    description: '',
    billingEmail: ''
  });

  const handleSubmit = async () => {
    setModalError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description.trim() || undefined,
        billingEmail: formData.billingEmail.trim()
      });
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to create organization');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  const isValidEmail = (email: string) => {
    return email.includes('@') && email.includes('.');
  };

  const isFormValid = () => {
    return (
      formData.name.trim().length > 0 &&
      formData.type.length > 0 &&
      isValidEmail(formData.billingEmail)
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Create Organization</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Organization Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Acme Records"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Organization Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white/30"
            >
              <option value="record_label">Record Label</option>
              <option value="publisher">Publisher</option>
              <option value="management">Management</option>
              <option value="production">Production</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of your organization..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Billing Email *</label>
            <input
              type="email"
              value={formData.billingEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, billingEmail: e.target.value }))}
              placeholder="billing@company.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
            <p className="text-white/40 text-xs mt-1">
              Used for invoices and billing notifications
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Organization'}
          </Button>
        </div>
      </div>
    </div>
  );
};
